import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

export type CustomDragNDropModalDragHandler = {
  onMouseDown: (e: React.MouseEvent) => void;
};

/** 기존: render prop 하나. 신규: 트리거는 `anchor(ref)`, 모달은 `content` — 버튼 아래 정렬에 ref만 넘기면 됨 */
export type CustomDragNDropModalChildren =
  | ((api: CustomDragNDropModalDragHandler) => ReactNode)
  | {
      anchor: (ref: React.RefObject<HTMLElement | null>) => ReactNode;
      content: (api: CustomDragNDropModalDragHandler) => ReactNode;
    };

function isSlottedChildren(
  children: CustomDragNDropModalChildren
): children is {
  anchor: (ref: React.RefObject<HTMLElement | null>) => ReactNode;
  content: (api: CustomDragNDropModalDragHandler) => ReactNode;
} {
  return (
    typeof children === "object" &&
    children !== null &&
    "anchor" in children &&
    "content" in children
  );
}

interface CustomDragNDropModalProps {
  children: CustomDragNDropModalChildren;
  portalTarget?: string;
  portalZIndex?: number;
  initialPosition?: {
    x?: number | string;
    y?: number | string;
  };
  containerSelector?: string;
  /** 슬롯 미사용 시: 외부 트리거에 붙인 ref — 모달을 이 요소 아래에 띄움 */
  targetRef?: React.RefObject<HTMLElement | null>;
  /** 슬롯 사용 시에만 의미 있음. false면 포털(모달)만 숨기고 `anchor`는 유지 */
  open?: boolean;
  /** 트리거 `getBoundingClientRect().bottom` 아래 간격(px) */
  anchorGapPx?: number;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}

export default function CustomDragNDropModal({
  children,
  portalTarget = ".c_page__container",
  portalZIndex = 10,
  initialPosition = { x: "20%", y: 90 },
  containerSelector = ".c_page__container",
  targetRef,
  open = true,
  anchorGapPx = 10,
  onDragStart,
  onDragEnd,
}: CustomDragNDropModalProps) {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const internalAnchorRef = useRef<HTMLElement | null>(null);
  const draggingRef = useRef(false);
  const initializedRef = useRef(false);
  const offsetRef = useRef({ x: 0, y: 0 });
  const [isPositioned, setIsPositioned] = useState(false);

  const slotted = isSlottedChildren(children);
  const positioningRef = slotted ? internalAnchorRef : targetRef;

  const getContainer = () =>
    document.querySelector(containerSelector) as HTMLElement | null;

  const clampToVisibleViewport = (
    x: number,
    y: number,
    modalWidth: number,
    modalHeight: number,
    container: HTMLElement
  ) => {
    const containerRect = container.getBoundingClientRect();
    const minX = containerRect.left;
    const minY = containerRect.top;
    const maxX = containerRect.right - modalWidth;
    const maxY = containerRect.bottom - modalHeight;

    return {
      x: Math.max(minX, Math.min(x, maxX)),
      y: Math.max(minY, Math.min(y, maxY)),
    };
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    const modal = modalRef.current;
    const containerDom = getContainer();
    if (!modal || !containerDom) return;

    const modalRect = modal.getBoundingClientRect();

    offsetRef.current = {
      x: e.clientX - modalRect.left,
      y: e.clientY - modalRect.top,
    };

    draggingRef.current = true;
    onDragStart?.();
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!draggingRef.current) return;

    const containerDom = getContainer();
    const modal = modalRef.current;
    if (!modal || !containerDom) return;

    const modalRect = modal.getBoundingClientRect();

    const newX = e.clientX - offsetRef.current.x;
    const newY = e.clientY - offsetRef.current.y;

    const { x: clampedX, y: clampedY } = clampToVisibleViewport(
      newX,
      newY,
      modalRect.width,
      modalRect.height,
      containerDom
    );

    modal.style.left = `${clampedX}px`;
    modal.style.top = `${clampedY}px`;
    modal.style.transform = "none";
  };

  const handleMouseUp = () => {
    if (draggingRef.current) {
      draggingRef.current = false;
      onDragEnd?.();
    }
  };

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  useEffect(() => {
    if (!open) {
      initializedRef.current = false;
      setIsPositioned(false);
    }
  }, [open]);

  const setInitialPosition = (
    modal: HTMLDivElement,
    containerDom: HTMLElement
  ) => {
    const modalRect = modal.getBoundingClientRect();
    const modalWidth = modalRect.width || modal.offsetWidth;
    const modalHeight = modalRect.height || modal.offsetHeight;

    if (modalWidth === 0 || modalHeight === 0) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const rect = modal.getBoundingClientRect();
          const width = rect.width || modal.offsetWidth;
          const height = rect.height || modal.offsetHeight;
          if (width > 0 && height > 0) {
            setInitialPosition(modal, containerDom);
          }
        });
      });
      return;
    }

    const containerRect = containerDom.getBoundingClientRect();

    let initialX: number;
    let initialY: number;

    const anchorEl = positioningRef?.current;
    if (anchorEl) {
      const targetRect = anchorEl.getBoundingClientRect();
      initialY = targetRect.bottom + anchorGapPx;
      initialX = targetRect.left + targetRect.width / 2 - modalWidth / 2;
    } else {
      if (
        typeof initialPosition.x === "string" &&
        initialPosition.x.includes("%")
      ) {
        const percent = parseFloat(initialPosition.x) / 100;
        initialX =
          containerRect.left + containerRect.width * percent - modalWidth / 2;
      } else {
        initialX =
          typeof initialPosition.x === "number"
            ? containerRect.left + initialPosition.x
            : containerRect.left + (containerRect.width - modalWidth) / 2;
      }

      if (
        typeof initialPosition.y === "string" &&
        initialPosition.y.includes("%")
      ) {
        const percent = parseFloat(initialPosition.y) / 100;
        initialY =
          containerRect.top + containerRect.height * percent - modalHeight / 2;
      } else {
        initialY =
          typeof initialPosition.y === "number"
            ? containerRect.top + initialPosition.y
            : containerRect.top + (containerRect.height - modalHeight) / 2;
      }
    }

    const { x: clampedX, y: clampedY } = clampToVisibleViewport(
      initialX,
      initialY,
      modalWidth,
      modalHeight,
      containerDom
    );

    modal.style.left = `${clampedX}px`;
    modal.style.top = `${clampedY}px`;
    modal.style.transform = "none";

    initializedRef.current = true;
    setIsPositioned(true);
  };

  useLayoutEffect(() => {
    if (!open) return;
    if (initializedRef.current) return;

    const modal = modalRef.current;
    const containerDom = getContainer();
    if (!modal || !containerDom) {
      requestAnimationFrame(() => {
        if (!open || initializedRef.current) return;
        const m = modalRef.current;
        const c = getContainer();
        if (m && c) {
          setInitialPosition(m, c);
        }
      });
      return;
    }

    // 슬롯 anchor ref는 같은 틱에 늦게 붙을 수 있음 (2프레임 후 재시도, 없으면 % 초기값)
    if (slotted && !positioningRef?.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!open || initializedRef.current) return;
          const m = modalRef.current;
          const c = getContainer();
          if (m && c) {
            setInitialPosition(m, c);
          }
        });
      });
      return;
    }

    setInitialPosition(modal, containerDom);
  }, [
    open,
    containerSelector,
    initialPosition.x,
    initialPosition.y,
    targetRef,
    slotted,
    anchorGapPx,
  ]);

  useEffect(() => {
    const modal = modalRef.current;
    const containerDom = getContainer();
    if (!modal || !containerDom) return;

    const clampCurrentPosition = () => {
      if (!initializedRef.current || draggingRef.current) return;

      const modalRect = modal.getBoundingClientRect();
      const currentLeft = parseFloat(modal.style.left || "0");
      const currentTop = parseFloat(modal.style.top || "0");

      const { x: clampedX, y: clampedY } = clampToVisibleViewport(
        Number.isFinite(currentLeft) ? currentLeft : 0,
        Number.isFinite(currentTop) ? currentTop : 0,
        modalRect.width,
        modalRect.height,
        containerDom
      );

      modal.style.left = `${clampedX}px`;
      modal.style.top = `${clampedY}px`;
      modal.style.transform = "none";
    };

    const observer = new ResizeObserver(clampCurrentPosition);
    observer.observe(modal);
    observer.observe(containerDom);
    window.addEventListener("resize", clampCurrentPosition);
    window.addEventListener("scroll", clampCurrentPosition, true);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", clampCurrentPosition);
      window.removeEventListener("scroll", clampCurrentPosition, true);
    };
  }, [containerSelector, open]);

  const portalRoot =
    typeof document !== "undefined"
      ? document.querySelector(portalTarget)
      : null;

  const dragApi: CustomDragNDropModalDragHandler = {
    onMouseDown: handleMouseDown,
  };

  const inner = slotted ? children.content(dragApi) : children(dragApi);

  const modalShell = (
    <div
      className="fixed inset-0 overflow-visible pointer-events-none"
      style={{ zIndex: portalZIndex }}
    >
      <div
        ref={modalRef}
        className="pointer-events-auto"
        style={{
          position: "fixed",
          opacity: isPositioned ? 1 : 0,
          transition: isPositioned ? "opacity 0.1s ease-in" : "none",
        }}
      >
        {inner}
      </div>
    </div>
  );

  const portaled = portalRoot
    ? createPortal(modalShell, portalRoot)
    : modalShell;

  return (
    <>
      {slotted ? children.anchor(internalAnchorRef) : null}
      {open ? portaled : null}
    </>
  );
}
